import { DeviceModuleTray } from '../../../domain/device-module-tray';
import { mapperPipe, PlatformType, remainderMapper } from 'osn-shared';
import {
  buttonMapperDomainToApi,
  countryTargetingMapper,
  descriptionLocalizedMapperDomainToApi,
  localizedBackgroundImagesMapper,
  platformTargetingMapper,
  scheduledDateMapperDomainToApi,
} from '../../mapping/create-tray-domain-to-api';
import { MarketingDeviceCtaPageItemApiModel } from '@cxb/editor-stb-api/model/models';
import {
  backgroundImagesMapperApiToDomain,
  buttonMapperApiToDomain,
  commonMapper,
  scheduledDateMapperApiToDomain,
} from '../../mapping/create-tray-api-to-domain';
import { DeviceModuleTrayFormViewModel } from './device-module-tray-form-view-model';
import { SchedulingType } from '../../../shared/enums/scheduling-type.enum';
import { TrayFormViewModelType } from '../view-model-common/view-model-type';
import {
  mapDomainButtonToButtonViewModel,
  mapLocalizedDomainImagesToPlatformLocalizedViewModelImage,
} from '../create-tray-screen/mapping/create-tray-domain-to-vm';
import {
  mapMarketingButtonToDomainButton,
  mapViewModelImagesToLocalizedDomainImages,
} from '../create-tray-screen/mapping/create-tray-vm-to-domain';

// 'DeviceModuleTray'

/**
 * DOMAIN -> API
 */

export const mapDomainDeviceModuleTrayToApi = (
  deviceModule: DeviceModuleTray,
  pageVersionId: string,
  tenantId?: string
  //@ts-ignore
  // TODO:MK BACKEND IS NOT READY
): CreateMarketingDeviceCtaPageItemApiModel => {
  return remainderMapper(
    mapperPipe(
      deviceModule,
      scheduledDateMapperDomainToApi,
      localizedBackgroundImagesMapper,
      descriptionLocalizedMapperDomainToApi,
      countryTargetingMapper,
      buttonMapperDomainToApi,
      platformTargetingMapper
    ),
    () => ({
      pageVersionId,
      titleLocalized: { en: deviceModule.titleEnglish, ar: deviceModule.titleArabic },
      countryIds: deviceModule.countryIds,
      draggable: deviceModule.draggable,
      internalName: deviceModule.internalName,
      id: deviceModule.id,
      order: deviceModule.order,
      titleEnglish: deviceModule.titleEnglish,
      titleArabic: deviceModule.titleArabic,
      type: 'DeviceModuleTray',
      userSegmentIds: deviceModule.userSegmentIds,
      visible: deviceModule.visible,
      tenantId,
    })
  );
};

/**
 * API -> DOMAIN
 */

export const mapApiDeviceModuleTrayToDomain = (deviceModule: MarketingDeviceCtaPageItemApiModel): DeviceModuleTray => {
  return remainderMapper(
    mapperPipe(deviceModule, commonMapper, scheduledDateMapperApiToDomain, backgroundImagesMapperApiToDomain, buttonMapperApiToDomain),
    () => ({
      title: deviceModule.titleLocalized,
      type: 'DeviceModuleTray',
      userSegmentIds: deviceModule.userSegments.map((userSegment) => userSegment.id),
      countryIds: deviceModule.countries.map((country) => country.id),
      countryGroups: deviceModule.countryGroups,
      titleEnglish: deviceModule.titleLocalized.en,
      titleArabic: deviceModule.titleLocalized.ar,
      descriptionTextArabic: deviceModule.descriptionLocalized.ar,
      descriptionTextEnglish: deviceModule.descriptionLocalized.en,
      marketingText: deviceModule.descriptionLocalized,
      internalName: deviceModule.internalName,
    })
  );
};

/**
 * DOMAIN -> VIEWMODEL
 */

export const mapDomainDeviceModuleTrayToViewModel = (domainModel: DeviceModuleTray): DeviceModuleTrayFormViewModel => {
  return {
    id: domainModel.id,
    button: mapDomainButtonToButtonViewModel(domainModel.primaryButton),
    countryIds: domainModel.countryIds,
    scheduling: {
      schedulingType: domainModel.scheduledFrom || domainModel.scheduledTo ? SchedulingType.Schedule : SchedulingType.LiveWhenPublished,
      scheduledFrom: domainModel.scheduledFrom ? new Date(domainModel.scheduledFrom).toISOString() : undefined,
      scheduledTo: domainModel.scheduledTo ? new Date(domainModel.scheduledTo).toISOString() : undefined,
    },
    title: { en: domainModel.titleEnglish, ar: domainModel.titleArabic },
    descriptionLocalized: { en: domainModel.descriptionTextEnglish, ar: domainModel.descriptionTextArabic },
    type: TrayFormViewModelType.DeviceModuleTray,
    userSegmentIds: [],
    backgroundImages: mapLocalizedDomainImagesToPlatformLocalizedViewModelImage(domainModel.backgroundImages),
    internalName: domainModel.internalName,
  };
};

/**
 * VIEWMODEL -> DOMAIN
 */

export const mapViewModelDeviceModuleTrayToDomain = (
  viewModel: DeviceModuleTrayFormViewModel,
  platformIds: Record<PlatformType, string>
): DeviceModuleTray => {
  return {
    id: viewModel.id,
    type: 'DeviceModuleTray',
    descriptionTextEnglish: viewModel.descriptionLocalized.en,
    titleArabic: viewModel.title.ar,
    titleEnglish: viewModel.title.en,
    descriptionTextArabic: viewModel.descriptionLocalized.ar,
    backgroundImages: mapViewModelImagesToLocalizedDomainImages(viewModel.backgroundImages, platformIds),
    userSegmentIds: viewModel.userSegmentIds,
    countryIds: viewModel.countryIds,
    scheduledFrom: viewModel.scheduling?.scheduledFrom ? new Date(viewModel.scheduling.scheduledFrom).getTime() : undefined,
    scheduledTo: viewModel.scheduling?.scheduledTo ? new Date(viewModel.scheduling.scheduledTo).getTime() : undefined,
    primaryButton: mapMarketingButtonToDomainButton(viewModel.button),
    order: undefined,
    visible: undefined,
    draggable: true,
    internalName: viewModel.internalName,
    platformIds: Object.values(platformIds),
  };
};
